import { ContentCopy, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    TableCell,
    TableRow,
    Tooltip
} from '@mui/material';
import { K8sAnalyzerNodesListTableNodeRowProps } from '../k8s-analyzer-types';
import { calculatePercentage, formatCPU, formatMemory, getCPUColor, getMemoryColor } from './k8s-analyzer-nodes-list.utils';

export function K8sAnalyzerNodesListTableNodeRow({
    node,
    isExpanded,
    onToggleExpand
}: K8sAnalyzerNodesListTableNodeRowProps) {
    const cpuPercentage = calculatePercentage(node.metrics.cpu.usage, node.metrics.cpu.capacity, 'cpu');
    const memPercentage = calculatePercentage(node.metrics.memory.usage, node.metrics.memory.capacity, 'memory');

    const displayValues = {
        status: node.status || 'Ready',
        role: node.role || '<none>',
        taints: node.taints || 0,
        version: node.version || 'v1.xx.x',
        pods: node.pods || 0,
        cpu: formatCPU(node.metrics.cpu.usage),
        mem: formatMemory(node.metrics.memory.usage),
        cpuPercent: Math.round(cpuPercentage),
        memPercent: Math.round(memPercentage),
        age: node.age || '?m'
    };

    const handleCopyNodeName = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(node.name);
    };

    return (
        <TableRow
            sx={{
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                cursor: 'pointer',
                ...(isExpanded ? { backgroundColor: 'rgba(0, 0, 0, 0.03)' } : {})
            }}
            onClick={onToggleExpand}
        >
            <TableCell padding="checkbox" align="center">
                <IconButton size="small">
                    {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
            </TableCell>
            <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {node.name}
                    </Box>
                    <Tooltip title="Copy node name">
                        <IconButton
                            size="small"
                            onClick={handleCopyNodeName}
                            sx={{ ml: 1 }}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </TableCell>
            <TableCell align="center">
                <Chip
                    label={displayValues.status}
                    size="small"
                    sx={{
                        backgroundColor: '#2196f3',
                        color: 'white',
                        borderRadius: '4px',
                        height: '22px',
                        fontSize: '0.75rem',
                        fontWeight: 'normal'
                    }}
                />
            </TableCell>
            <TableCell>{displayValues.role}</TableCell>
            <TableCell align="center">{displayValues.taints}</TableCell>
            <TableCell align="center">{displayValues.version}</TableCell>
            <TableCell align="center" >{displayValues.pods}</TableCell>
            <TableCell align="center" >{displayValues.cpu}</TableCell>
            <TableCell align="center" >{displayValues.mem}</TableCell>
            <TableCell align="center"
                sx={{
                    color: getCPUColor(displayValues.cpuPercent),
                    fontWeight: displayValues.cpuPercent > 70 ? 'bold' : 'normal'
                }}
            >
                {displayValues.cpuPercent}
            </TableCell>
            <TableCell align="center"
                sx={{
                    color: getMemoryColor(displayValues.memPercent),
                    fontWeight: displayValues.memPercent > 70 ? 'bold' : 'normal'
                }}
            >
                {displayValues.memPercent}
            </TableCell>
            <TableCell align="center" >{formatCPU(node.metrics.cpu.capacity)}</TableCell>
            <TableCell align="center" >{formatMemory(node.metrics.memory.capacity)}</TableCell>
            <TableCell align="center" >{displayValues.age}</TableCell>
        </TableRow>
    );
} 